'use client';

import { useEffect, useMemo, useState } from 'react';

import { defaultLocale } from '@/i18n/config';
import { ELocalStorageKeys, EQueryKeys } from '@/lib/enums';
import { OfferData } from '@/lib/types';
import { getLanguageNameFromIsoCode } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';
import { webStorageUtil } from '@/state/webStorage.state.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useAxios from './useAxios';

// TODO: create the type??!!!
interface OffersData {
  offers: {
    bundles: object[];
    popups: OfferData[];
    promotions: OfferData[];
    rolling: object[];
    progressBars: OfferData[];
  };
  balances: object[];
  sections: object[];
  profileFrameImg?: string;
  playerLevelImg?: string;
  playerLevel?: {
    assetUrl: string;
    text?: string;
    endsIn?: number | null;
  };
  publisherSettings?: {
    popUpStartHourUTC: number
  }
}

const filterByOfferIds = (arr: any[] | undefined, idSet: Set<string>, includeSections: boolean): any[] =>
  (arr ?? []).filter((item) => item.offerId != null && idSet.has(item.offerId)).map(({ offerSectionId, ...rest }) => includeSections ? { ...rest, offerSectionId } : rest);

export const useOffers = ({ offerIds, includeSections = true }: { offerIds?: string[], includeSections?: boolean }) => {
  const [hasNoOffers, setHasNoOffers] = useState<boolean>(false);
  const { currentLanguage, languagesList, playerData } = useAppState();
  const playerId = playerData?.playerId;

  const { get } = useAxios();

  const { fetchQuery, invalidateQueries } = useQueryClient();

  const languageName = useMemo(
    () => getLanguageNameFromIsoCode(currentLanguage, languagesList, defaultLocale.language),
    [currentLanguage, languagesList]
  );

  const getOffers = useQuery<OffersData, Error>({
    queryKey: [EQueryKeys.OFFERS, playerId, currentLanguage, offerIds ? offerIds.join(',') : undefined],
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      try {
        const response = await get(`/store/offers`, false, { language: languageName }, {}, false);

        // if (webStorageUtil.get(ELocalStorageKeys.MOCK_OFFERS_API)) {
        //   return JSON.parse(webStorageUtil.get(ELocalStorageKeys.MOCK_OFFERS_API));
        // }

        if (offerIds && offerIds.length > 0 && response?.offers) {
          const idSet = new Set(offerIds);

          return {
            ...response,
            offers: {
              ...response.offers,
              bundles: filterByOfferIds(response.offers.bundles, idSet, includeSections),
              popups: filterByOfferIds(response.offers.popups, idSet, includeSections),
              promotions: filterByOfferIds(response.offers.promotions, idSet, includeSections),
              rolling: filterByOfferIds(response.offers.rolling, idSet, includeSections),
              progressBars: filterByOfferIds(response.offers.progressBars, idSet, includeSections)
            }
          };
        }

        return response;
      } catch (err) {
        console.error('Error fetching offers:', err);

        // customEvents.sendCustomEvent(
        //   EEventsType.ERROR,
        //   {
        //     type: 'api error',
        //     route: 'getOffers',
        //     error: JSON.stringify(err)
        //   },
        //   EStorePhase.POST_LOGIN
        // );
        // throw err;

        throw err; // Re-throw the error to be handled by React Query
      }
    },
    enabled: false
  });

  const getProductsAssets = (arrProductIds: string[]) => {
    const idsUrlParam = encodeURIComponent(arrProductIds.join(','));

    return fetchQuery({
      queryKey: [EQueryKeys.PRODUCTS, idsUrlParam, currentLanguage],
      queryFn: async () => {
        if (!arrProductIds || !arrProductIds.length) return [];

        return get(
          `store/products/external-ids/${idsUrlParam}`,
          false,
          { language: languageName },
          {},
          false
        ).catch((err) => {
          //   customEvents.sendCustomEvent(
          //     EEventsType.ERROR,
          //     {
          //       type: 'api error',
          //       route: 'getProducts',
          //       error: JSON.stringify(err)
          //     },
          //     EStorePhase.OTHER
          //   );
          //   logToNewRelic(err);
          throw err;
        });
      },
      gcTime: 60000,
      staleTime: 60000
    });
  };

  const getStoreCampaign = useMutation({
    mutationFn: async (data: { tokenId: string; linkId?: string; org?: string }) => {
      return await get(
        `/store/campaign/${data.tokenId}`,
        false,
        { language: languageName },
        {},
        false
      );
    }
  });

  useEffect(() => {
    setHasNoOffers(
      !!getOffers.data &&
      webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN) &&
      getOffers.data.offers?.bundles?.length === 0 &&
      getOffers.data.offers?.promotions?.length === 0
    );
  }, [getOffers.data]);

  return { getOffers, getProductsAssets, getStoreCampaign, hasNoOffers };
};
